import {
  Box,
  ButtonBase,
  CircularProgress,
  Fade,
  IconButton,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useEffect } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  formatDetailAmount,
  formatDetailDate,
  getExplorerUrl,
  getStatusLabel,
  STATUS_META,
  TYPE_META,
} from "../../components/pages/history/helpers";
import { getOrderById, HistoryOrder } from "../../services/orders/history";
import { useTelegram } from "../../hooks";
import { useTranslation } from "react-i18next";

export function TransactionDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const { t } = useTranslation("history");

  useEffect(() => {
    const handleBack = () => navigate(-1);
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handleBack);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handleBack);
    };
  }, [navigate, tg]);

  const { data, isLoading, isError } = useQuery({
    queryFn: () => getOrderById(orderId!),
    queryKey: ["order", orderId],
    enabled: Boolean(orderId),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: "60px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ marginTop: "60px", textAlign: "center", padding: "0 24px" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {t("details.load_failed_title")}
        </Typography>
        <Typography sx={{ marginTop: "8px", color: "text.secondary" }}>
          {t("details.load_failed_subtitle")}
        </Typography>
      </Box>
    );
  }

  return <TransactionDetailContent order={data} onBack={() => navigate(-1)} />;
}

interface TransactionDetailContentProps {
  order: HistoryOrder;
  onBack: () => void;
}

function TransactionDetailContent({ order, onBack }: TransactionDetailContentProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("history");
  const typeMeta = TYPE_META[order.type];
  const statusMeta = STATUS_META[order.status];
  const statusLabel = getStatusLabel(order.status, t);

  // ADJUSTMENT — направление определяем по факту, а не по meta:
  // положительная дельта → buy_*, отрицательная → sell_*.
  const isAdjustmentCredit =
    order.type === "ADJUSTMENT" && !!order.buy_amount && !!order.buy_currency;
  const isAdjustmentDebit =
    order.type === "ADJUSTMENT" && !!order.sell_amount && !!order.sell_currency;

  const isIncoming = typeMeta.direction === "in" || isAdjustmentCredit;
  const isSwap = typeMeta.direction === "swap";

  const primaryAmount = isSwap
    ? order.buy_amount
    : isAdjustmentCredit
      ? order.buy_amount
      : isAdjustmentDebit
        ? order.sell_amount
        : order.sell_amount ?? order.buy_amount;
  const primaryCurrency = isSwap
    ? order.buy_currency
    : isAdjustmentCredit
      ? order.buy_currency
      : isAdjustmentDebit
        ? order.sell_currency
        : order.sell_currency ?? order.buy_currency;
  const primarySign = isIncoming ? "+" : isSwap ? "" : "−";
  const primaryColor = isIncoming ? "#22C55E" : isSwap ? undefined : undefined;

  const copy = (label: string, value: string | null | undefined) => {
    if (!value) return;
    try {
      navigator.clipboard.writeText(value);
      enqueueSnackbar(t("details.copied", { label }), { variant: "success" });
    } catch {
      enqueueSnackbar(t("details.copy_failed"), { variant: "error" });
    }
  };

  const explorerUrl = getExplorerUrl(order.network_code, order.external_tx_hash);
  const openExplorer = () => {
    if (!explorerUrl) return;
    try {
      Telegram.WebApp.openLink(explorerUrl);
    } catch {
      window.open(explorerUrl, "_blank");
    }
  };

  return (
    <Fade in>
      <Box sx={{ marginBottom: explorerUrl ? "110px" : "40px", width: "100%" }}>
        {/* Хедер: назад + дата + пустой правый бокс для симметрии */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "18px",
            marginTop: "8px",
          }}
        >
          <IconButton
            onClick={onBack}
            sx={(theme) => ({
              width: 36,
              height: 36,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.06)"
                  : "rgba(0, 0, 0, 0.05)",
            })}
          >
            <Icon icon="solar:alt-arrow-left-linear" width={18} height={18} />
          </IconButton>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.secondary" }}>
            {formatDetailDate(order.created_at)}
          </Typography>
          <Box sx={{ width: 36 }} />
        </Box>

        {/* Иконка типа + плашка статуса */}
        <Box sx={{ display: "flex", justifyContent: "center", marginBottom: "10px" }}>
          <Box sx={{ position: "relative" }}>
            <Box
              sx={(theme) => ({
                width: 72,
                height: 72,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.05)",
                color: theme.palette.primary.main,
              })}
            >
              <Icon icon={typeMeta.icon} width={36} height={36} color="currentColor" />
            </Box>
            <StatusBadge statusColor={statusMeta.color} statusGroup={statusMeta.group} />
          </Box>
        </Box>

        {/* Крупная сумма */}
        <Typography
          sx={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: 800,
            color: primaryColor ?? "text.primary",
            marginBottom: "18px",
          }}
        >
          {primarySign} {formatDetailAmount(primaryAmount)} {primaryCurrency ?? ""}
        </Typography>

        {/* Плашка статуса, если не успех */}
        {statusMeta.group !== "success" && (
          <Box sx={{ display: "flex", justifyContent: "center", marginBottom: "14px" }}>
            <Box
              sx={{
                padding: "6px 12px",
                borderRadius: "999px",
                bgcolor: statusMeta.bg,
                color: statusMeta.color,
                fontSize: "12px",
                fontWeight: 700,
              }}
            >
              {statusLabel}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "grid", gap: "10px" }}>
          {/* Сумма к зачислению / полученная сумма / комиссия */}
          <DetailBlock>
            {isSwap ? (
              <>
                <DetailRow
                  label={t("details.gave")}
                  value={`${formatDetailAmount(order.sell_amount)} ${order.sell_currency ?? ""}`.trim()}
                />
                <DetailRow
                  label={t("details.received")}
                  value={`${formatDetailAmount(order.buy_amount)} ${order.buy_currency ?? ""}`.trim()}
                />
                {order.rate && (
                  <DetailRow
                    label={t("details.rate")}
                    value={formatDetailAmount(order.rate, 6)}
                  />
                )}
              </>
            ) : (
              <>
                {order.buy_amount && (
                  <DetailRow
                    label={t("details.amount_credited")}
                    value={`${formatDetailAmount(order.buy_amount)} ${order.buy_currency ?? order.sell_currency ?? ""}`.trim()}
                  />
                )}
                {order.sell_amount && (
                  <DetailRow
                    label={
                      isIncoming
                        ? t("details.amount_received")
                        : t("details.amount_sent")
                    }
                    value={`${formatDetailAmount(order.sell_amount)} ${order.sell_currency ?? ""}`.trim()}
                  />
                )}
              </>
            )}
            {order.fee_amount && Number(order.fee_amount) > 0 && (
              <DetailRow
                label={t("details.fee")}
                value={`${formatDetailAmount(order.fee_amount)} ${order.fee_currency ?? ""}`.trim()}
                icon="solar:question-circle-bold-duotone"
              />
            )}
          </DetailBlock>

          {/* Сеть / контрагент */}
          {(order.network_code || order.address) && (
            <DetailBlock>
              {order.network_code && (
                <DetailRow
                  label={t("details.network")}
                  value={order.network_code}
                  valueIcon="solar:diamond-bold-duotone"
                />
              )}
              {order.address && (
                <DetailRow
                  label={
                    order.type === "WITHDRAW"
                      ? t("details.recipient")
                      : t("details.sender")
                  }
                  value={shortenMiddle(order.address)}
                  copyValue={order.address}
                  onCopy={() => copy(t("details.address_label"), order.address)}
                />
              )}
            </DetailBlock>
          )}

          {/* ID транзакции / hash */}
          <DetailBlock>
            <DetailRow
              label={t("details.tx_id")}
              value={`ZERO-${order.id}`}
              copyValue={order.id}
              onCopy={() => copy(t("details.id_label"), order.id)}
            />
            {order.external_tx_hash && (
              <DetailRow
                label={t("details.tx_hash")}
                value={shortenMiddle(order.external_tx_hash)}
                copyValue={order.external_tx_hash}
                onCopy={() =>
                  copy(t("details.hash_label"), order.external_tx_hash)
                }
              />
            )}
          </DetailBlock>

          {order.comment && (
            <DetailBlock>
              <DetailRow label={t("details.comment")} value={order.comment} />
            </DetailBlock>
          )}
        </Box>

        {/* Sticky footer с кнопками */}
        {explorerUrl && (
          <Box
            sx={(theme) => ({
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              padding: "12px 16px 20px",
              display: "flex",
              gap: "10px",
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(180deg, rgba(15,15,18,0) 0%, #0F0F12 25%)"
                  : "linear-gradient(180deg, rgba(245,245,247,0) 0%, #F5F5F7 25%)",
              zIndex: 100,
            })}
          >
            <ButtonBase
              onClick={openExplorer}
              sx={(theme) => ({
                flex: 1,
                padding: "16px",
                borderRadius: "18px",
                bgcolor: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                color: theme.palette.mode === "dark" ? "#000000" : "#FFFFFF",
                fontWeight: 700,
                fontSize: "15px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              })}
            >
              <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
                {t("details.open_explorer")}
              </Typography>
              <Icon
                icon="solar:arrow-right-up-linear"
                width={16}
                height={16}
                color="currentColor"
              />
            </ButtonBase>
            <ButtonBase
              onClick={() => copy(t("details.link_label"), explorerUrl)}
              sx={(theme) => ({
                width: 56,
                height: 56,
                borderRadius: "18px",
                bgcolor: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                color: theme.palette.mode === "dark" ? "#000000" : "#FFFFFF",
              })}
            >
              <Icon icon="solar:copy-bold-duotone" width={20} height={20} color="currentColor" />
            </ButtonBase>
          </Box>
        )}
      </Box>
    </Fade>
  );
}

function DetailBlock({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        padding: "6px 14px",
        borderRadius: "18px",
        bgcolor: theme.palette.secondary.main,
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.06)"
            : "none",
      })}
    >
      {children}
    </Box>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
  icon?: string;
  valueIcon?: string;
  copyValue?: string;
  onCopy?: () => void;
}

function DetailRow({ label, value, icon, valueIcon, copyValue, onCopy }: DetailRowProps) {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "10px 0",
        borderBottom: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.05)"
        }`,
        "&:last-of-type": { borderBottom: "none" },
      })}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
          {label}
        </Typography>
        {icon && (
          <Box sx={{ color: "text.secondary", display: "flex" }}>
            <Icon icon={icon} width={14} height={14} color="currentColor" />
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
        {valueIcon && (
          <Icon icon={valueIcon} width={16} height={16} color="#F5C542" />
        )}
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
        {copyValue && (
          <IconButton
            onClick={onCopy}
            size="small"
            sx={{ padding: "4px", color: "text.secondary" }}
          >
            <Icon icon="solar:copy-linear" width={16} height={16} color="currentColor" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function StatusBadge({
  statusColor,
  statusGroup,
}: {
  statusColor: string;
  statusGroup: "success" | "processing" | "error";
}) {
  const icon =
    statusGroup === "success"
      ? "solar:check-circle-bold"
      : statusGroup === "processing"
        ? "solar:clock-circle-bold"
        : "solar:close-circle-bold";
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        right: -4,
        top: -4,
        width: 26,
        height: 26,
        borderRadius: "999px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: theme.palette.background.default,
      })}
    >
      <Icon icon={icon} width={22} height={22} color={statusColor} />
    </Box>
  );
}

function shortenMiddle(s: string): string {
  if (s.length <= 14) return s;
  return `${s.slice(0, 6)}…${s.slice(-7)}`;
}
